import React, { useEffect, useState, useRef } from 'react';
import { MessageCircle } from 'react-feather';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useMessagingSocket } from 'providers/messaging';
import { useAuth } from 'lib/auth';

// 🔊 Notification sound
const notificationSound = new Audio('/sounds/new_message.mp3');

export const MessageCount = () => {
  const { user } = useAuth();
  const { unreadCounts, lastMessage } = useMessagingSocket();
  const [totalUnread, setTotalUnread] = useState(0);
  const [pulse, setPulse] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [lastNotifiedMessage, setLastNotifiedMessage] = useState(null);

  const location = useLocation();
  const isOnConversationPage = location.pathname.includes('/app/conversations');
  const firstRender = useRef(true);

  // ✅ Calculate total unread messages
  useEffect(() => {
    const count = Object.values(unreadCounts || {}).reduce((a, b) => a + b, 0);
    setTotalUnread(count);
  }, [unreadCounts]);

  // ✅ Request Notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // ✅ Handle new incoming messages (sound + desktop notification)
  useEffect(() => {
    if (firstRender.current) {
      // skip sound on initial render (page load)
      firstRender.current = false;
      return;
    }

    if (!lastMessage || !lastMessage._id) return;

    const isSelf = lastMessage.senderId === user?._id;
    const isDuplicate = lastMessage._id === lastNotifiedMessage;
    const shouldPlay = !isSelf && !isDuplicate && !isOnConversationPage && totalUnread > 0;

    if (shouldPlay) {
      setPulse(true);
      setLastNotifiedMessage(lastMessage._id);

      // ✅ Play notification sound
      notificationSound.volume = 0.5;
      notificationSound.play().catch(() => {});

      // ✅ Desktop Notification
      if ('Notification' in window && Notification.permission === 'granted') {
        const { content, senderName } = lastMessage;
        new Notification(senderName || 'New message', {
          body: content || 'You have a new message',
          icon: '/favicon.ico',
        });
      }

      const t = setTimeout(() => setPulse(false), 1000);
      return () => clearTimeout(t);
    }
  }, [lastMessage, totalUnread, isOnConversationPage, user?._id, lastNotifiedMessage]);

  return (
    <div
      className="relative mx-6 group"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <Link to="/app/conversations">
        <motion.div
          animate={pulse ? { scale: [1, 1.2, 1] } : { scale: 1 }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <MessageCircle
            className="text-white hover:text-blue-200 transition-colors duration-200 drop-shadow-sm"
            size={24}
          />

          {/* 🔴 Unread Badge */}
          <AnimatePresence>
            {totalUnread > 0 && (
              <motion.div
                key="badge"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-red-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full shadow-md min-w-[16px] text-center"
              >
                {totalUnread > 9 ? '9+' : totalUnread}
              </motion.div>
            )}
          </AnimatePresence>

          {/* 🪄 Tooltip */}
          <AnimatePresence>
            {showTooltip && totalUnread > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2 }}
                className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-white/90 text-gray-800 text-xs px-2 py-1 rounded-md shadow-md border border-gray-100 whitespace-nowrap"
              >
                {totalUnread === 1 ? '1 new message' : `${totalUnread} new messages`}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </Link>
    </div>
  );
};
